import { useState, useCallback, useEffect, useRef } from 'react';
import { adaptiveEngine, tagMasteryCalculator, adaptiveSessionStateManager } from '@services';
import { QUESTION_REASON } from '@config/constants';

/**
 * Hook for managing adaptive question selection and flow
 * Implements the PRD 5-priority adaptive selection algorithm
 * Leverages tag mastery for HamTestOnline-style adaptive question selection
 * 
 * Integrates with adaptive session state for:
 * - Dynamic question group sizing based on performance
 * - Remediation injection tracking
 * - Linear progression state management
 */
export function useQuestionFlow(userId, courseId, currentSectionId, sessionId = null) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionReason, setQuestionReason] = useState(null); // Why was this question selected?
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [sessionQuestions, setSessionQuestions] = useState([]); // For tracking session state
  const [questionHistory, setQuestionHistory] = useState([]);
  const [tagMasteryMap, setTagMasteryMap] = useState({}); // Cache of tag mastery values
  const [dynamicGroupInfo, setDynamicGroupInfo] = useState(null); // Track dynamic sizing info
  
  // Performance optimization: prefetch next question
  const [prefetchedQuestion, setPrefetchedQuestion] = useState(null);
  const [prefetchInProgress, setPrefetchInProgress] = useState(false);
  const [prefetchExhausted, setPrefetchExhausted] = useState(false);
  const [lastMasteryRefresh, setLastMasteryRefresh] = useState(0);
  const previousSectionIdRef = useRef(null);
  const prefetchedQuestionRef = useRef(null);
  const prefetchInProgressRef = useRef(false);

  useEffect(() => {
    prefetchedQuestionRef.current = prefetchedQuestion;
  }, [prefetchedQuestion]);

  useEffect(() => {
    prefetchInProgressRef.current = prefetchInProgress;
  }, [prefetchInProgress]);

  /**
   * Refresh tag mastery cache for improved question selection
   * PERFORMANCE: Only refreshes if cache is older than 2 minutes
   */
  const refreshTagMastery = useCallback(async (forceRefresh = false) => {
    if (!userId || !courseId) {
      return tagMasteryMap;
    }

    // Skip refresh if cache is fresh (< 2 minutes old)
    const now = Date.now();
    if (!forceRefresh && tagMasteryMap && Object.keys(tagMasteryMap).length > 0 && (now - lastMasteryRefresh) < 120000) {
      console.log('[useQuestionFlow] Using cached tag mastery (fresh)');
      return tagMasteryMap;
    }

    try {
      const allTagMasteries = await tagMasteryCalculator.getAllTagMasteries(userId, courseId);
      const masteryMap = {};

      allTagMasteries.forEach(tm => {
        masteryMap[tm.tagId] = {
          finalMastery: tm.finalMastery,
          bucket: tm.bucket,
          rawMastery: tm.rawMastery,
          decayFactor: tm.decayFactor,
        };
      });

      setTagMasteryMap(masteryMap);
      setLastMasteryRefresh(now);
      console.log('[useQuestionFlow] Tag mastery refreshed:', Object.keys(masteryMap).length, 'tags');
      return masteryMap;
    } catch (err) {
      console.warn('[useQuestionFlow] Error refreshing tag mastery:', err);
      return tagMasteryMap || {};
    }
  }, [userId, courseId, tagMasteryMap, lastMasteryRefresh]);

  /**
   * Select next adaptive question using true PRD algorithm
   * Implements 5-priority selection: decay > time > struggle > preview > current
   * Enhanced with HamTestOnline tag mastery for better candidate weighting
   * 
   * Integrates dynamic question group sizing if session provided
   * PERFORMANCE: Uses prefetch for instant question transitions
   */
  const selectNextQuestion = useCallback(async (options = {}) => {
    if (!userId || !courseId || !currentSectionId) {
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // INSTANT UI: Use prefetched question if available
      if (prefetchedQuestion && !options.forceFresh) {
        console.log('[useQuestionFlow] ⚡ Using prefetched question (instant)');
        const cached = prefetchedQuestion;
        setPrefetchedQuestion(null); // Clear cache
        setPrefetchExhausted(false);
        
        setCurrentQuestion(cached.question);
        setQuestionReason(cached.reason);
        setSessionQuestions(prev => [...prev, { 
          questionId: cached.question.id, 
          reason: cached.reason, 
          tags: cached.question.tags 
        }]);
        setQuestionHistory(prev => [...prev, cached.question.id]);
        
        setLoading(false);
        
        // Start prefetching next question in background
        setTimeout(() => prefetchNextQuestion(), 100);
        
        return cached;
      }

      // No prefetch available - fetch now (slow path)
      console.log('[useQuestionFlow] No prefetch - fetching question (slow path)');
      const result = await fetchQuestion(options);
      if (result) {
        setCurrentQuestion(result.question);
        setQuestionReason(result.reason);
        setSessionQuestions(prev => [...prev, {
          questionId: result.question.id,
          reason: result.reason,
          tags: result.question.tags,
        }]);
        setQuestionHistory(prev => [...prev, result.question.id]);
        setPrefetchExhausted(false);
      } else {
        // Ensure stale question state is cleared so auto-prefetch cannot loop
        setCurrentQuestion(null);
        setQuestionReason(null);
      }
      
      // Start prefetching next question
      if (result) {
        setTimeout(() => prefetchNextQuestion(), 100);
      }
      
      return result;
    } catch (err) {
      console.error('Error selecting next question:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, currentSectionId, sessionId, prefetchedQuestion]);

  /**
   * Internal function to fetch question (shared by select and prefetch)
   * PERFORMANCE: Uses cached tag mastery, skips unnecessary refreshes
   */
  const fetchQuestion = useCallback(async (options = {}) => {
    // Use cached tag mastery if fresh, otherwise refresh
    const masteryMap = await refreshTagMastery(false);

    const excludeIds = [
      ...answeredQuestions,
      ...(options.excludeQuestionIds || []),
    ];

    // Skip dynamic sizing lookup if not needed (saves DB query)
    let groupInfo = dynamicGroupInfo; // Use cached if available
    if (sessionId && !groupInfo) {
      try {
        groupInfo = await adaptiveEngine.getDynamicQuestionLimits(sessionId);
        setDynamicGroupInfo(groupInfo);
      } catch (err) {
        console.warn('[useQuestionFlow] Error getting dynamic limits:', err);
      }
    }

    // Call the true adaptive engine with 5-priority algorithm
    const nextQuestion = await adaptiveEngine.selectNextAdaptiveQuestion(
      userId,
      courseId,
      currentSectionId,
      {
        excludeQuestionIds: excludeIds,
        warmUpMode: options.warmUpMode || false,
        reinforcementMode: options.reinforcementMode || false,
        tagMasteryMap: masteryMap,
        sessionId,
      }
    );

    if (!nextQuestion) {
      console.log('[useQuestionFlow] No more questions available');
      return null;
    }

    const reason = nextQuestion.reason || QUESTION_REASON.CURRENT;
    const actualQuestion = nextQuestion.question || nextQuestion;

    return {
      question: actualQuestion,
      reason: reason,
      dynamicGroupInfo: groupInfo,
    };
  }, [userId, courseId, currentSectionId, answeredQuestions, sessionId, refreshTagMastery, dynamicGroupInfo]);

  /**
   * Prefetch next question in background (non-blocking)
   */
  const prefetchNextQuestion = useCallback(async () => {
    if (!userId || !courseId || !currentSectionId) return;
    if (prefetchInProgressRef.current || prefetchedQuestionRef.current || prefetchExhausted) return;

    prefetchInProgressRef.current = true;
    setPrefetchInProgress(true);
    try {
      const startTime = performance.now();
      const result = await fetchQuestion();
      
      if (result) {
        const duration = performance.now() - startTime;
        console.log(`[useQuestionFlow] 🚀 Prefetch completed in ${duration.toFixed(0)}ms`);
        setPrefetchExhausted(false);
        setPrefetchedQuestion(result);
      } else {
        // No candidate available right now - stop auto-retrying until state changes
        setPrefetchExhausted(true);
      }
    } catch (err) {
      console.debug('[useQuestionFlow] Prefetch failed (non-critical):', err);
    } finally {
      prefetchInProgressRef.current = false;
      setPrefetchInProgress(false);
    }
  }, [userId, courseId, currentSectionId, prefetchExhausted, fetchQuestion]);

  /**
   * Select question from current section only (no adaptive algorithm)
   */
  const selectSectionQuestion = useCallback(async (sectionId) => {
    if (!userId || !courseId) {
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const question = await adaptiveEngine.selectQuestionFromSection(
        userId,
        courseId,
        sectionId || currentSectionId,
        answeredQuestions
      );

      setCurrentQuestion(question);
      setQuestionReason(QUESTION_REASON.CURRENT);

      if (question) {
        setQuestionHistory(prev => [...prev, question.id]);
      }

      return question;
    } catch (err) {
      console.error('Error selecting section question:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, currentSectionId, answeredQuestions]);

  /**
   * Mark question as answered
   */
  const markAnswered = useCallback((questionId) => {
    setAnsweredQuestions(prev => {
      if (!prev.includes(questionId)) {
        return [...prev, questionId];
      }
      return prev;
    });
    setPrefetchExhausted(false);
  }, []);

  // When section changes, clear state
  useEffect(() => {
    if (currentSectionId === previousSectionIdRef.current) {
      return;
    }

    if (!currentSectionId) {
      previousSectionIdRef.current = null;
      return;
    }

    console.log('[useQuestionFlow] Section changed to:', currentSectionId);
    setCurrentQuestion(null);
    setQuestionReason(null);
    setAnsweredQuestions([]);
    setSessionQuestions([]);
    setQuestionHistory([]);
    setPrefetchedQuestion(null);
    setPrefetchExhausted(false);
    setError(null);
    previousSectionIdRef.current = currentSectionId;
  }, [currentSectionId]);

  /**
   * Reset flow (for new session)
   */
  const resetFlow = useCallback(() => {
    setCurrentQuestion(null);
    setQuestionReason(null);
    setAnsweredQuestions([]);
    setSessionQuestions([]);
    setQuestionHistory([]);
    setPrefetchExhausted(false);
    setError(null);
  }, []);

  /**
   * Check if section should be unlocked
   */
  const checkSectionUnlock = useCallback(async (completedSectionId) => {
    if (!userId || !courseId) {
      return null;
    }

    try {
      const unlockedSectionId = await adaptiveEngine.checkSectionUnlock(
        userId,
        courseId,
        completedSectionId
      );

      return unlockedSectionId;
    } catch (err) {
      console.error('Error checking section unlock:', err);
      return null;
    }
  }, [userId, courseId]);

  /**
   * Get learning recommendations
   */
  const getRecommendations = useCallback(async () => {
    if (!userId || !courseId) {
      return null;
    }

    try {
      const recommendations = await adaptiveEngine.getLearningRecommendations(
        userId,
        courseId
      );

      return recommendations;
    } catch (err) {
      console.error('Error getting recommendations:', err);
      return null;
    }
  }, [userId, courseId]);

  /**
   * Get session question summary
   */
  const getSessionSummary = useCallback(() => {
    const summary = {
      totalQuestions: sessionQuestions.length,
      byReason: {},
    };

    sessionQuestions.forEach(item => {
      summary.byReason[item.reason] = (summary.byReason[item.reason] || 0) + 1;
    });

    return summary;
  }, [sessionQuestions]);

  /**
   * Auto-prefetch next question when current question changes
   */
  useEffect(() => {
    if (currentQuestion && !prefetchedQuestion && !prefetchInProgress && !prefetchExhausted) {
      console.log('[useQuestionFlow] 🚀 Auto-starting prefetch');
      const timer = setTimeout(() => {
        prefetchNextQuestion();
      }, 200); // Small delay to let question render
      return () => clearTimeout(timer);
    }
  }, [currentQuestion, prefetchedQuestion, prefetchInProgress, prefetchExhausted, prefetchNextQuestion]);

  return {
    currentQuestion,
    questionReason,
    loading,
    error,
    answeredQuestions,
    sessionQuestions,
    questionHistory,
    tagMasteryMap,
    dynamicGroupInfo,
    selectNextQuestion,
    selectSectionQuestion,
    markAnswered,
    resetFlow,
    checkSectionUnlock,
    getRecommendations,
    getSessionSummary,
    refreshTagMastery,
    // Performance indicators
    prefetchReady: !!prefetchedQuestion,
    prefetchInProgress,
  };
}

/**
 * Hook for managing question timer
 */
export function useQuestionTimer() {
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    let interval;

    if (isRunning && startTime) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRunning, startTime]);

  const start = useCallback(() => {
    setStartTime(Date.now());
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    return elapsedTime;
  }, [elapsedTime]);

  const reset = useCallback(() => {
    setStartTime(null);
    setElapsedTime(0);
    setIsRunning(false);
  }, []);

  return {
    elapsedTime,
    isRunning,
    start,
    stop,
    reset,
  };
}
